import { Search, Tags, X } from "lucide-react";
import { useGetIdentity, useTranslate } from "ra-core";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { getSaleName, useActiveSales } from "../useActiveSales";
import { PRIORITIES } from "../cardModel";
import { CreatableCombobox } from "../CreatableCombobox";
import { FilterSelect } from "../FilterSelect";
import {
  countActiveCardFilters,
  parseCardFilters,
  setSearchParam,
} from "../filters";
import { useSourcesByUsage } from "../useSourcesByUsage";
import { useTagsByUsage } from "../useTagsByUsage";

const FILTER_KEYS = ["q", "user", "tags", "source", "priority"];
const SEARCH_DEBOUNCE_MS = 300;

export const BoardFilters = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = parseCardFilters(searchParams);
  const sales = useActiveSales();
  const { tags } = useTagsByUsage();
  const sources = useSourcesByUsage();
  const activeCount = countActiveCardFilters(filters);

  const set = (key: string, value: string | number[] | null) =>
    setSearchParams((params) => setSearchParam(params, key, value), {
      replace: true,
    });

  // The search hits the server: wait for a pause in typing
  const [search, setSearch] = useState(filters.q);
  useEffect(() => {
    if (search === filters.q) return;
    const timeout = setTimeout(() => set("q", search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const clearFilters = () => {
    setSearch("");
    setSearchParams(
      (params) =>
        FILTER_KEYS.reduce(
          (next, key) => setSearchParam(next, key, null),
          params,
        ),
      { replace: true },
    );
  };

  const toggleTag = (tagId: number) =>
    set(
      "tags",
      filters.tagIds.includes(tagId)
        ? filters.tagIds.filter((id) => id !== tagId)
        : [...filters.tagIds, tagId],
    );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={translate("crm.operations.filters.search")}
          aria-label={translate("crm.operations.filters.search")}
          className="h-8 w-52 pl-8"
        />
      </div>
      <FilterSelect
        label={translate("crm.operations.filters.user")}
        anyLabel={translate("crm.operations.filters.all_users")}
        value={filters.salesId}
        onChange={(value) => set("user", value)}
        options={[
          ...(identity
            ? [
                {
                  value: String(identity.id),
                  label: translate("crm.operations.filters.mine"),
                },
              ]
            : []),
          ...sales
            .filter((sale) => sale.id !== identity?.id)
            .map((sale) => ({
              value: String(sale.id),
              label: getSaleName(sale),
            })),
        ]}
      />
      <CreatableCombobox
        options={tags.map((tag) => ({
          value: String(tag.id),
          label: tag.name,
          color: tag.color,
          count: tag.nb_contacts,
        }))}
        selected={filters.tagIds.map(String)}
        onSelect={(tagId) => toggleTag(Number(tagId))}
        placeholder={translate("crm.operations.placeholders.search_tags")}
      >
        <Button
          variant="outline"
          size="sm"
          className={filters.tagIds.length > 0 ? "h-8 border-primary" : "h-8"}
          aria-label={translate("crm.operations.filters.tags")}
        >
          <Tags />
          {translate("crm.operations.filters.tags")}
          {filters.tagIds.length > 0 && ` (${filters.tagIds.length})`}
        </Button>
      </CreatableCombobox>
      <FilterSelect
        label={translate("crm.operations.filters.source")}
        anyLabel={`${translate("crm.operations.filters.source")}: ${translate("crm.operations.filters.any")}`}
        value={filters.source}
        onChange={(value) => set("source", value)}
        options={sources.map((source) => ({
          value: source.name,
          label: source.name,
        }))}
      />
      <FilterSelect
        label={translate("crm.operations.filters.priority")}
        anyLabel={`${translate("crm.operations.filters.priority")}: ${translate("crm.operations.filters.any")}`}
        value={filters.priority}
        onChange={(value) => set("priority", value)}
        options={PRIORITIES.map((priority) => ({
          value: priority,
          label: translate(`crm.operations.priorities.${priority}`),
        }))}
      />
      {activeCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8"
          onClick={clearFilters}
        >
          <X />
          {translate("crm.operations.filters.clear")}
        </Button>
      )}
    </div>
  );
};
